/**
 * LookupAdmin — admin page for managing fund classification dropdown values.
 * Manages: Fund Areas, Fund Strategies (per area), and Product Types.
 */
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../Auth/AuthContext'
import { api } from '../../lib/api'

function LookupSection({ title, description, items, onAdd, onUpdate, onDelete, renderExtra }) {
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')

  function handleAdd(e) {
    e.preventDefault()
    if (!newName.trim()) return
    onAdd(newName.trim())
    setNewName('')
  }

  function startEdit(item) {
    setEditingId(item.id)
    setEditName(item.name)
  }

  function handleSave(id) {
    if (!editName.trim()) return
    onUpdate(id, editName.trim())
    setEditingId(null)
  }

  return (
    <div className="rounded-xl border border-edge bg-surface-secondary p-4 sm:p-6">
      <h3 className="text-base font-semibold text-ink mb-1">{title}</h3>
      {description && <p className="text-xs text-ink-muted mb-4">{description}</p>}

      <div className="space-y-2 mb-4">
        {items.length === 0 && (
          <p className="text-sm text-ink-subtle italic">No items yet. Add one below.</p>
        )}
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-2 group">
            {editingId === item.id ? (
              <>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave(item.id)
                    if (e.key === 'Escape') setEditingId(null)
                  }}
                  className="flex-1 rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink focus:ring-2 focus:ring-brand focus:border-transparent"
                  autoFocus
                />
                <button
                  onClick={() => handleSave(item.id)}
                  className="text-xs text-brand hover:text-brand/80 px-2 py-1"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  className="text-xs text-ink-muted hover:text-ink px-2 py-1"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-ink">{item.name}</span>
                {renderExtra && renderExtra(item)}
                <button
                  onClick={() => startEdit(item)}
                  className="text-xs text-ink-muted hover:text-ink sm:opacity-0 sm:group-hover:opacity-100 transition px-2 py-1"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(item.id)}
                  className="text-xs text-danger-fg hover:text-danger-fg/80 sm:opacity-0 sm:group-hover:opacity-100 transition px-2 py-1"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={handleAdd} className="flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Add new..."
          className="flex-1 rounded-md border border-edge bg-surface px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-brand focus:border-transparent"
        />
        <button
          type="submit"
          disabled={!newName.trim()}
          className="px-3 py-1.5 bg-brand text-ink-onAccent rounded-md text-sm hover:opacity-90 disabled:opacity-40 transition"
        >
          Add
        </button>
      </form>
    </div>
  )
}

export default function LookupAdmin() {
  const { currentOrg } = useAuth()
  const [areas, setAreas] = useState([])
  const [strategies, setStrategies] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const orgId = currentOrg?.id

  const loadAll = useCallback(async () => {
    if (!orgId) return
    setError(null)
    try {
      const [a, s, pt] = await Promise.all([
        api.get(`/lookups/${orgId}/areas`),
        api.get(`/lookups/${orgId}/strategies`),
        api.get(`/lookups/${orgId}/product-types`),
      ])
      setAreas(a)
      setStrategies(s)
      setProductTypes(pt)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => { loadAll() }, [loadAll])

  // ── Area CRUD ──
  async function addArea(name) {
    try {
      await api.post(`/lookups/${orgId}/areas`, { name, sort_order: areas.length })
      loadAll()
    } catch (e) { setError(e.message) }
  }

  async function updateArea(id, name) {
    try {
      await api.put(`/lookups/${orgId}/areas/${id}`, { name })
      loadAll()
    } catch (e) { setError(e.message) }
  }

  async function deleteArea(id) {
    try {
      await api.delete(`/lookups/${orgId}/areas/${id}`)
      loadAll()
    } catch (e) { setError(e.message) }
  }

  // ── Strategy CRUD ──
  async function addStrategy(name, areaId) {
    const areaStrategies = strategies.filter(s => s.fund_area_id === areaId)
    try {
      await api.post(`/lookups/${orgId}/strategies`, {
        name, fund_area_id: areaId, sort_order: areaStrategies.length,
      })
      loadAll()
    } catch (e) { setError(e.message) }
  }

  async function updateStrategy(id, name) {
    try {
      await api.put(`/lookups/${orgId}/strategies/${id}`, { name })
      loadAll()
    } catch (e) { setError(e.message) }
  }

  async function deleteStrategy(id) {
    try {
      await api.delete(`/lookups/${orgId}/strategies/${id}`)
      loadAll()
    } catch (e) { setError(e.message) }
  }

  // ── Product Type CRUD ──
  async function addProductType(name) {
    try {
      await api.post(`/lookups/${orgId}/product-types`, { name, sort_order: productTypes.length })
      loadAll()
    } catch (e) { setError(e.message) }
  }

  async function updateProductType(id, name) {
    try {
      await api.put(`/lookups/${orgId}/product-types/${id}`, { name })
      loadAll()
    } catch (e) { setError(e.message) }
  }

  async function deleteProductType(id) {
    try {
      await api.delete(`/lookups/${orgId}/product-types/${id}`)
      loadAll()
    } catch (e) { setError(e.message) }
  }

  if (loading) {
    return <div className="p-6 text-ink-muted">Loading admin settings...</div>
  }

  return (
    <div className="py-2 sm:py-4 max-w-3xl">
      <h2 className="text-xl sm:text-2xl font-bold text-ink mb-2">Fund Classifications</h2>
      <p className="text-sm text-ink-muted mb-6">
        Manage the dropdown options available in Fund Variables. Changes apply immediately.
      </p>

      {error && (
        <div className="mb-4 px-3 py-2 bg-danger/20 border border-danger-border rounded-lg text-sm text-danger-fg">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <LookupSection
          title="Fund Areas"
          description="Top-level classification (e.g., Private Capital, Real Assets)."
          items={areas}
          onAdd={addArea}
          onUpdate={updateArea}
          onDelete={deleteArea}
        />

        {/* Strategies grouped by area */}
        {areas.map((area) => {
          const areaStrategies = strategies.filter(s => s.fund_area_id === area.id)
          return (
            <LookupSection
              key={area.id}
              title={`Strategies — ${area.name}`}
              description={`Fund strategies available when "${area.name}" is selected as the area.`}
              items={areaStrategies}
              onAdd={(name) => addStrategy(name, area.id)}
              onUpdate={updateStrategy}
              onDelete={deleteStrategy}
            />
          )
        })}

        <LookupSection
          title="Product Types"
          description="Classification for core vs specialty products."
          items={productTypes}
          onAdd={addProductType}
          onUpdate={updateProductType}
          onDelete={deleteProductType}
        />
      </div>
    </div>
  )
}
